
const lang = document.querySelector('html').getAttribute('lang')

export const DEFAULT_CALENDAR_TIME_ZONE = 'Europe/Paris'
export const CALENDAR_TIME_ZONE = DEFAULT_CALENDAR_TIME_ZONE
const CALENDAR_TIME_ZONE_STORAGE_KEY = 'reflexosteo.calendarTimeZone'
const CALENDAR_TIME_ZONE_PROMPT_KEY = 'reflexosteo.calendarTimeZonePrompt'

function storageGet (key) {
  try {
    return window.localStorage.getItem(key)
  } catch (error) {
    return null
  }
}

function storageSet (key, value) {
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
    // Ignore quota / private-mode errors
  }
}

export function browserTimeZone () {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_CALENDAR_TIME_ZONE
}

export function calendarTimeZone () {
  return storageGet(CALENDAR_TIME_ZONE_STORAGE_KEY) || DEFAULT_CALENDAR_TIME_ZONE
}

export function setCalendarTimeZone (timeZone) {
  storageSet(CALENDAR_TIME_ZONE_STORAGE_KEY, timeZone)
}

export function calendarTimeZonePrompt () {
  const browser = browserTimeZone()
  const current = calendarTimeZone()
  if (!browser || browser === current) return null
  if (storageGet(CALENDAR_TIME_ZONE_PROMPT_KEY) === browser) return null

  return { browser, current }
}

export function dismissCalendarTimeZonePrompt () {
  storageSet(CALENDAR_TIME_ZONE_PROMPT_KEY, browserTimeZone())
}

export function calendarTimeZoneCity (timeZone) {
  return (timeZone || DEFAULT_CALENDAR_TIME_ZONE).split('/').pop().replace(/_/g, ' ')
}

function calendarFormatParts (dateInput, options = {}) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
  const { timeZone = calendarTimeZone(), ...intlOptions } = options

  return new Intl.DateTimeFormat('fr-FR', {
    timeZone,
    hour12: false,
    hourCycle: 'h23',
    ...intlOptions
  }).formatToParts(date)
}

function calendarPart (parts, type) {
  return parts.find((part) => part.type === type)?.value || ''
}

export function calendarZonedDateParts (dateInput) {
  const parts = calendarFormatParts(dateInput, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })

  const year = calendarPart(parts, 'year')
  const month = calendarPart(parts, 'month').padStart(2, '0')
  const day = calendarPart(parts, 'day').padStart(2, '0')
  const hour = calendarPart(parts, 'hour').padStart(2, '0')
  const minute = calendarPart(parts, 'minute').padStart(2, '0')

  return {
    isoDate: `${year}-${month}-${day}`,
    date: `${day}/${month}/${year}`,
    time: `${hour}:${minute}`,
    weekday: (dateInput instanceof Date ? dateInput : new Date(dateInput))
      .toLocaleDateString('fr-FR', { timeZone: calendarTimeZone(), weekday: 'long' })
      .toUpperCase()
  }
}

export function calendarWeekday (dateInput) {
  const { isoDate } = calendarZonedDateParts(dateInput)
  const [year, month, day] = isoDate.split('-').map(Number)

  return new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay()
}

export function calendarDateForWeekday (wday, reference = new Date()) {
  const { isoDate } = calendarZonedDateParts(reference)
  const currentWday = calendarWeekday(`${isoDate}T12:00:00+00:00`)
  let delta = wday - currentWday
  if (wday === 0) delta += 7

  const [year, month, day] = isoDate.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, month - 1, day + delta, 12))

  return shifted.toISOString().slice(0, 10)
}

export function calendarTimeZoneOffsetLabel (date = new Date(), timeZone = calendarTimeZone()) {
  const raw = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short'
  }).formatToParts(date instanceof Date ? date : new Date(date))
  const offset = calendarPart(raw, 'timeZoneName') || 'UTC+2'

  return offset
    .replace(/GMT/i, 'UTC')
    .replace(/UTC([+-])(\d)(?!\d)/, 'UTC$10$2')
    .replace(/:00$/, '')
}

export function updateCalendarTimeZoneLabel (root, date = new Date()) {
  const axis = root?.querySelector('.fc-col-header .fc-timegrid-axis')
  if (!axis) return

  const frame = axis.querySelector('.fc-timegrid-axis-frame') || axis
  let label = frame.querySelector('[data-calendar-tz-label]')
  if (!label) {
    label = document.createElement('span')
    label.className = 'fc-timegrid-axis-cushion'
    label.dataset.calendarTzLabel = ''
    frame.append(label)
  }

  label.textContent = calendarTimeZoneOffsetLabel(date)
}

export function currentScrollTime () {
  const parts = calendarFormatParts(new Date(), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })

  return [
    calendarPart(parts, 'hour').padStart(2, '0'),
    calendarPart(parts, 'minute').padStart(2, '0'),
    calendarPart(parts, 'second').padStart(2, '0')
  ].join(':')
}

export function calendarWeekdayHeaderContent (arg) {
  return {
    html: `<span class="fc-day-header-name">${calendarZonedDateParts(arg.date).weekday}</span>`
  }
}

export function calendarDayHeaderContent (arg) {
  const zoned = calendarZonedDateParts(arg.date)

  return {
    html: `${calendarWeekdayHeaderContent(arg).html}<span class="fc-day-header-num">${Number(zoned.isoDate.slice(-2))}</span>`
  }
}

function slotMinutesFromMidnight (arg) {
  if (typeof arg?.time?.milliseconds === 'number') {
    return Math.round(arg.time.milliseconds / 60000)
  }

  const date = arg?.date instanceof Date ? arg.date : new Date(arg?.date)
  if (Number.isNaN(date.getTime())) return null

  return (date.getUTCHours() * 60) + date.getUTCMinutes()
}

export function calendarSlotLabelContent (arg) {
  const minutes = slotMinutesFromMidnight(arg)
  if (minutes === null || minutes % 60 !== 0) return ''

  return `${String(minutes / 60).padStart(2, '0')}h`
}

export const calendarEventTimeFormat = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  meridiem: false,
  omitZeroMinute: false
}

export function calendarEventTimeText (date) {
  if (!date) return ''

  return calendarZonedDateParts(date).time
}

export function calendarEventDateTime (value) {
  if (!value) return value

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toISOString()
}

export function calendarEventContent (arg) {
  if (arg.event.display === 'background' || arg.event.allDay) return

  const time = document.createElement('div')
  time.className = 'fc-event-time'
  time.textContent = calendarEventTimeText(arg.event.start)

  const title = document.createElement('div')
  title.className = 'fc-event-title'
  title.textContent = arg.event.title || ''

  const frame = document.createElement('div')
  frame.className = 'fc-event-main-frame'
  frame.append(time, title)

  return { domNodes: [frame] }
}

export function centerCalendarOnCurrentTime (calendar, root) {
  const scroller = root.querySelector('.fc-timegrid-body')?.closest('.fc-scroller')
  if (!scroller) return

  const nowLine = root.querySelector('.fc-timegrid-now-indicator-line')
  if (nowLine) {
    const nowOffset = nowLine.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
    scroller.scrollTop = Math.max(0, nowOffset - (scroller.clientHeight / 2))
    return
  }

  calendar.scrollToTime(currentScrollTime())
}

export function renderCalendarCentered (calendar, root) {
  calendar.render()
  updateCalendarTimeZoneLabel(root)
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => centerCalendarOnCurrentTime(calendar, root))
  })
}

// Each calendar passes its own `plugins` (loaded on demand: load_full_calendar.js).
export default {
  initialView: 'timeGridWeek',
  timeZone: CALENDAR_TIME_ZONE,
  scrollTime: currentScrollTime(),
  slotMinTime: '00:00:00',
  slotMaxTime: '24:00:00',
  slotDuration: '00:30:00',
  slotLabelInterval: '01:00:00',
  slotLabelContent: calendarSlotLabelContent,
  eventTimeFormat: calendarEventTimeFormat,
  dayHeaderContent: (arg) => {
    if (arg.view.type.startsWith("timeGrid")) return calendarDayHeaderContent(arg)

    return calendarWeekdayHeaderContent(arg)
  },
  views: {
    dayGridMonth: {
      eventContent: calendarEventContent
    }
  },
  scrollTimeReset: false,
  contentHeight: 720,
  locale: lang,
  editable: false,
  selectable: true,
  selectOverlap (event) {
    return event.rendering === 'background'
  },
  allDaySlot: false,
  headerToolbar: {
    left: 'prev,next today',
    center: 'title',
    right: 'listWeek timeGridDay,timeGridWeek,dayGridMonth'
  },
  nowIndicator: true,
  datesSet (info) {
    updateCalendarTimeZoneLabel(info.view.calendar.el)
  }
}
